/**
 * WIDTH CALCULATOR UTILITY
 * Intelligently calculates optimal column widths
 * @version 1.0.0
 * @production-ready
 */

import { ColumnDefinition, TableStyleConfig, TableData, ColumnType } from '../types';

// Constants for width calculations
const CHAR_WIDTH_RATIO = 0.7;              // Character width relative to font size
const PADDING_BUFFER = 30;                 // Additional padding per column
const MIN_COLUMN_WIDTH = 60;               // Absolute minimum width
const MAX_COLUMN_WIDTH = 400;              // Absolute maximum width
const SAMPLE_SIZE = 50;                    // Number of rows to sample for content

/**
 * Calculate optimal width for a single column
 */
export const calculateColumnWidth = (
  column: ColumnDefinition,
  data: TableData,
  styleConfig: TableStyleConfig
): number => {
  // Return fixed width if specified
  if (typeof column.width === 'number') {
    return clampWidth(column.width, column.minWidth, column.maxWidth);
  }
  
  if (column.width === 'fixed') {
    return clampWidth(120, column.minWidth, column.maxWidth);
  }

  const condensed = styleConfig.condensed ?? true;
  const baseFontSize = styleConfig.baseFontSize ?? (condensed ? 11 : 14);
  const headerFontSize = styleConfig.headerFontSize ?? (condensed ? 10 : 13);
  
  // Calculate header width
  const headerText = condensed && column.abbreviation ? column.abbreviation : column.label;
  const headerWidth = calculateTextWidth(headerText, headerFontSize);
  
  // Calculate content width by sampling data
  const contentWidth = calculateContentWidth(column, data, baseFontSize);
  
  // Start with larger of header or content
  let calculatedWidth = Math.max(headerWidth, contentWidth) + PADDING_BUFFER;
  
  // Apply type-specific adjustments
  calculatedWidth = applyTypeAdjustments(calculatedWidth, column.type, condensed);
  
  // Apply constraints
  return clampWidth(calculatedWidth, column.minWidth, column.maxWidth);
};

/**
 * Calculate text width in pixels
 */
const calculateTextWidth = (text: string, fontSize: number): number => {
  return text.length * (fontSize * CHAR_WIDTH_RATIO);
};

/**
 * Calculate content width by sampling data
 */
const calculateContentWidth = (
  column: ColumnDefinition,
  data: TableData,
  fontSize: number
): number => {
  const rowKeys = Object.keys(data);
  const sampleSize = Math.min(rowKeys.length, SAMPLE_SIZE);
  let maxWidth = 0;
  
  for (let i = 0; i < sampleSize; i++) {
    const rowKey = rowKeys[i];
    const value = data[rowKey]?.[column.key];
    const displayValue = formatValueForWidth(value, column);
    const width = calculateTextWidth(displayValue, fontSize);
    maxWidth = Math.max(maxWidth, width);
  }
  
  return maxWidth;
};

/**
 * Format value for width calculation
 */
const formatValueForWidth = (value: any, column: ColumnDefinition): string => {
  if (value == null || value === '') return '';
  
  if (column.format) {
    return column.format(value);
  }
  
  switch (column.type) {
    case 'number':
    case 'calculated':
      return typeof value === 'number' ? value.toFixed(2) : String(value);
    case 'date':
      return '2024-12-31'; // Sample date for width calculation
    case 'time':
      return '23:59'; // Sample time
    default:
      return String(value);
  }
};

/**
 * Apply type-specific width adjustments
 */
const applyTypeAdjustments = (
  width: number,
  type: ColumnDefinition['type'],
  condensed: boolean
): number => {
  const adjustments: { [K in ColumnType]: number } = {
    date: condensed ? 110 : 150,
    time: condensed ? 90 : 120,
    number: condensed ? 80 : 110,
    calculated: condensed ? 90 : 120,
    select: condensed ? 100 : 130,
    text: condensed ? 100 : 140,
    readonly: condensed ? 90 : 120,
    action: 40 // Delete button column - fixed small width
  };
  
  return Math.max(width, adjustments[type as ColumnType] ?? width);
};

/**
 * Clamp width within constraints
 */
const clampWidth = (
  width: number,
  minWidth?: number,
  maxWidth?: number
): number => {
  let clamped = Math.max(MIN_COLUMN_WIDTH, width);
  clamped = Math.min(MAX_COLUMN_WIDTH, clamped);
  
  if (minWidth !== undefined) {
    clamped = Math.max(minWidth, clamped);
  }
  
  if (maxWidth !== undefined) {
    clamped = Math.min(maxWidth, clamped);
  }
  
  return Math.round(clamped);
};

/**
 * Calculate all column widths at once
 */
export const calculateAllColumnWidths = (
  columns: ColumnDefinition[],
  data: TableData,
  styleConfig: TableStyleConfig
): number[] => {
  return columns
    .filter(col => !col.hidden)
    .map(col => calculateColumnWidth(col, data, styleConfig));
};

/**
 * Calculate sticky column left position
 */
export const calculateStickyLeft = (
  columns: ColumnDefinition[],
  widths: number[],
  currentIndex: number
): number => {
  let left = 0;
  for (let i = 0; i < currentIndex; i++) {
    if (columns[i].sticky && !columns[i].hidden) {
      left += widths[i];
    }
  }
  return left;
};

/**
 * Calculate optimal header font size to fit column width
 */
export const calculateHeaderFontSize = (
  labelLength: number,
  columnWidth: number,
  baseHeaderFontSize: number
): number => {
  const estimatedWidth = calculateTextWidth(
    'A'.repeat(labelLength),
    baseHeaderFontSize
  );
  
  if (estimatedWidth > columnWidth - PADDING_BUFFER) {
    const ratio = (columnWidth - PADDING_BUFFER) / estimatedWidth;
    return Math.max(8, Math.floor(baseHeaderFontSize * ratio));
  }
  
  return baseHeaderFontSize;
};

/**
 * Calculate total table width
 */
export const calculateTotalTableWidth = (columnWidths: number[]): number => {
  return columnWidths.reduce((sum, width) => sum + width, 0);
};
